import posthog from 'posthog-js';

/**
 * Centralized analytics layer over posthog-js.
 *
 * Every funnel event flows through `captureEvent` keyed by an
 * `ANALYTICS_EVENTS` value — the single source of truth for event names, so
 * there are no stringly-typed typos and the taxonomy stays consistent. All
 * helpers no-op when `NEXT_PUBLIC_POSTHOG_KEY` is absent (keyless/dev builds),
 * mirroring the init guard in `_app.tsx`.
 *
 * Conventions: fire events from hooks (success/error callbacks survive
 * component unmount) and keep property keys snake_case (PostHog convention).
 */
export const ANALYTICS_EVENTS = {
  SIGNUP_CTA_CLICKED: 'signup_cta_clicked',
  PLAN_SELECTION_VIEWED: 'plan_selection_viewed',
  PLAN_SELECTED: 'plan_selected',
  SIGNUP_FORM_VIEWED: 'signup_form_viewed',
  SIGNUP_VALIDATION_FAILED: 'signup_validation_failed',
  SIGNUP_SUBMITTED: 'signup_submitted',
  SIGNUP_SUCCEEDED: 'signup_succeeded',
  SIGNUP_FAILED: 'signup_failed',
  EMAIL_VERIFICATION_SUBMITTED: 'email_verification_submitted',
  EMAIL_VERIFIED: 'email_verified',
  SIGNIN_SUCCEEDED: 'signin_succeeded',
  SIGNIN_FAILED: 'signin_failed',
  COUPON_APPLIED: 'coupon_applied',
  PROMO_VIEWED: 'promo_viewed',
  UPGRADE_DIALOG_OPENED: 'upgrade_dialog_opened',
  UPGRADE_SUBMITTED: 'upgrade_submitted',
  UPGRADE_SUCCEEDED: 'upgrade_succeeded',
  UPGRADE_FAILED: 'upgrade_failed',
  CIRCLE_ALERTS_BADGE_SHOWN: 'circle_alerts_badge_shown',
  CIRCLE_ALERTS_BELL_OPENED: 'circle_alerts_bell_opened',
  CIRCLE_ALERT_CLICKED: 'circle_alert_clicked',
  CIRCLE_ALERTS_EXPANDED: 'circle_alerts_expanded'
} as const;

type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

const isEnabled = (): boolean => Boolean(process.env.NEXT_PUBLIC_POSTHOG_KEY);

/**
 * Options for {@link captureEvent}.
 *
 * `immediate` is for events fired right before a hard navigation
 * (`window.location.href`/`assign`), where the default batched queue can be
 * torn down before it flushes. It skips the batch and sends via `sendBeacon`,
 * which the browser delivers even as the document unloads. Soft `router.push`
 * navigations don't need this — the posthog instance survives the transition.
 */
type CaptureOptions = {
  immediate?: boolean;
};

/**
 * Capture a funnel event. `environment` is always attached so prod insights
 * can filter out dev/test noise sharing the same PostHog project.
 */
export const captureEvent = (
  event: AnalyticsEvent,
  properties?: AnalyticsProperties,
  options?: CaptureOptions
): void => {
  if (!isEnabled()) {
    return;
  }
  try {
    posthog.capture(
      event,
      {
        ...properties,
        environment: process.env.NODE_ENV
      },
      options?.immediate
        ? { send_instantly: true, transport: 'sendBeacon' }
        : undefined
    );
  } catch {
    // Analytics must never surface errors to users.
  }
};

/**
 * Identify the signed-in customer by their stable numeric id. Short-circuits
 * when already identified as that user so authenticated navigation doesn't
 * re-identify on every page. PostHog merges the prior anonymous distinct_id,
 * retroactively linking pre-signup funnel events to the converted person.
 */
export const identifyUser = (
  distinctId: string,
  props?: AnalyticsProperties
): void => {
  if (!isEnabled()) {
    return;
  }
  try {
    if (posthog.get_distinct_id() === distinctId) {
      return;
    }
    posthog.identify(distinctId, props);
  } catch {
    // Analytics must never surface errors to users.
  }
};

/**
 * Register `tier` as a super property so it auto-attaches to every subsequent
 * event without each call site having to pass it.
 */
export const setTier = (tier: string): void => {
  if (!isEnabled()) {
    return;
  }
  try {
    posthog.register({ tier });
  } catch {
    // Analytics must never surface errors to users.
  }
};

/**
 * Clear identity and super properties on sign-out so the next anonymous
 * visitor isn't attributed to the prior user.
 */
export const resetAnalytics = (): void => {
  if (!isEnabled()) {
    return;
  }
  try {
    posthog.reset();
  } catch {
    // Analytics must never surface errors to users.
  }
};
